import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FillOutcomeKind, PageSessionSnapshot } from '@oysterjs/smartfill-contracts';
import { useSidebarSession } from '../bridge/session';
import { useKeyedBridgeAction } from './data';
import type { DisplayError } from '../bridge/errors';

type PasteAction = 'once' | 'start-continuous' | 'continue' | 'finish';

/**
 * The dock's post-paste line: how many fields landed, or, when nothing was
 * written, the outcome's own explanation instead of "Pasted 0 fields".
 */
type PasteConfirmation =
  | { kind: 'pasted'; count: number; bucketLabel: string }
  | { kind: 'outcome'; message: string };

/** The tab and page-session revision a fill request was issued against. */
interface RequestStart {
  tab: string | null;
  revision: number;
}

interface PasteOnceOptions {
  /** Section paste: resolve against this entity's subtree only. */
  scopeEntityId?: string | null;
  /** Human name of the pasted section, for the confirmation line. */
  scopeLabel?: string;
}

interface PasteContextValue {
  busy: PasteAction | null;
  canPaste: boolean;
  continuous: boolean;
  confirmation: PasteConfirmation | null;
  error: DisplayError | null;
  /** Non-error FYI, e.g. a fill that completed after the user left its tab. */
  notice: string | null;
  pasteOnce: (options?: PasteOnceOptions) => Promise<void>;
  startContinuous: () => Promise<void>;
  continueContinuous: () => Promise<void>;
  finishContinuous: () => Promise<void>;
  clearError: () => void;
}

const WRITTEN_OUTCOMES = new Set<FillOutcomeKind>(['SUCCESS', 'PARTIAL']);

const PasteContext = createContext<PasteContextValue | null>(null);

export function PasteProvider({ children }: { children: ReactNode }) {
  const { client, snapshot, getSnapshot, snapshotRevision, applyPageSession } = useSidebarSession();
  const action = useKeyedBridgeAction<PasteAction>();
  const busy = action.busy;
  const [confirmation, setConfirmation] = useState<PasteConfirmation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const confirmationTimer = useRef<number | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const continuous =
    snapshot.pageSession.mode === 'CONTINUOUS' &&
    snapshot.pageSession.continuous !== null &&
    snapshot.pageSession.continuous.status !== 'FINISHED';
  const canPaste =
    snapshot.auth.status === 'signed-in' &&
    snapshot.tab.status === 'ready' &&
    snapshot.activeBucket !== null &&
    busy === null;

  useEffect(() => {
    setConfirmation(null);
    if (confirmationTimer.current !== null) window.clearTimeout(confirmationTimer.current);
    confirmationTimer.current = null;
  }, [snapshot.activeBucket?.bucketId]);

  useEffect(
    () => () => {
      if (confirmationTimer.current !== null) window.clearTimeout(confirmationTimer.current);
      if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    },
    []
  );

  const showNotice = (message: string) => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = window.setTimeout(() => {
      setNotice(null);
      noticeTimer.current = null;
    }, 6_000);
  };

  // A snapshot for a tab the user already left must not repaint the current
  // tab's view; when it carries the background-completion warning, surface
  // that as a notice instead.
  const applyOrNotice = (result: PageSessionSnapshot, started: RequestStart): boolean => {
    if (
      applyPageSession(result, {
        expectedTabContextId: started.tab,
        sinceRevision: started.revision
      })
    ) {
      return true;
    }
    if (result.warning?.code === 'COMPLETED_IN_BACKGROUND') showNotice(result.warning.message);
    return false;
  };

  const requestStart = (): RequestStart => ({
    tab: getSnapshot().tab.contextId,
    revision: snapshotRevision('pageSession')
  });

  const showConfirmation = (result: PageSessionSnapshot, bucketLabel: string) => {
    if (confirmationTimer.current !== null) window.clearTimeout(confirmationTimer.current);
    const outcome = result.outcome;
    setConfirmation(
      outcome && !WRITTEN_OUTCOMES.has(outcome.kind)
        ? { kind: 'outcome', message: outcome.summary }
        : { kind: 'pasted', count: result.counts.filled, bucketLabel }
    );
    confirmationTimer.current = window.setTimeout(() => {
      setConfirmation(null);
      confirmationTimer.current = null;
    }, 2_000);
  };

  const pasteOnce = async (options?: PasteOnceOptions) => {
    // Whole-bucket pastes are meaningless during a continuous session (the
    // session already owns the page), but a scoped section paste augments it.
    if (!canPaste || (continuous && options?.scopeEntityId === undefined)) return;
    const current = getSnapshot();
    const started = requestStart();
    setNotice(null);
    await action.run('once', 'Could not fill this page.', async () => {
      const result = await client.request('fill.startOnce', {
        ...(options?.scopeEntityId !== undefined ? { scopeEntityId: options.scopeEntityId } : {})
      });
      if (!applyOrNotice(result, started)) return;
      const label = options?.scopeLabel ?? current.activeBucket?.label ?? result.source?.label;
      if (label) showConfirmation(result, label);
    });
  };

  const startContinuous = async () => {
    if (!canPaste || continuous) return;
    const started = requestStart();
    setConfirmation(null);
    setNotice(null);
    await action.run('start-continuous', 'Could not start continuous fill.', async () => {
      applyOrNotice(await client.request('fill.startContinuous', {}), started);
    });
  };

  const continueContinuous = async () => {
    if (busy || !continuous) return;
    const started = requestStart();
    setNotice(null);
    await action.run('continue', 'Could not continue filling.', async () => {
      applyOrNotice(await client.request('fill.continueContinuous', {}), started);
    });
  };

  const finishContinuous = async () => {
    if (busy || !continuous) return;
    const started = requestStart();
    setNotice(null);
    await action.run('finish', 'Could not finish continuous fill.', async () => {
      const result = await client.request('fill.finishContinuous', {});
      if (!applyOrNotice(result, started)) return;
      // Stop revokes the run and can return an empty projection. Its counts
      // cannot describe how many answers the completed run wrote.
      if (confirmationTimer.current !== null) window.clearTimeout(confirmationTimer.current);
      setConfirmation({ kind: 'outcome', message: 'Continuous fill stopped.' });
      confirmationTimer.current = window.setTimeout(() => {
        setConfirmation(null);
        confirmationTimer.current = null;
      }, 2_000);
    });
  };

  const value: PasteContextValue = {
    busy,
    canPaste,
    continuous,
    confirmation,
    error: action.error,
    notice,
    pasteOnce,
    startContinuous,
    continueContinuous,
    finishContinuous,
    clearError: action.clearError
  };

  return <PasteContext.Provider value={value}>{children}</PasteContext.Provider>;
}

export function usePaste(): PasteContextValue {
  const value = useContext(PasteContext);
  if (!value) throw new Error('usePaste must be used inside PasteProvider.');
  return value;
}
